import { useAsyncEffect } from '@/hooks';
import { downloadBlob } from '@/lib/utils';
import { useStores } from '@/store';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { Grid } from 'semantic-ui-react';
import { Authority, OnboardingFormEmployee, toID } from 'sol-data';
import { FileDisplayCard } from '../FileDisplayCard';
import { MultipleFilesPreviewModal } from '../MultipleFilesPreviewModal';

export const OnboardingSubmissionDetailsForms = observer(() => {
  const { onboardingFormEmployeeStore, temporaryEmployeeStore, onboardingFormEmployeeFileStore } =
    useStores();
  const [selectedFormEmployee, setSelectedFormEmployee] = useState<OnboardingFormEmployee>();
  const [showFilesModal, setShowFilesModal] = useState(false);

  useEffect(() => {
    return () => {
      onboardingFormEmployeeStore.clear();
    };
  }, []);

  useAsyncEffect(async () => {
    if (temporaryEmployeeStore.current) {
      await onboardingFormEmployeeStore.fetchByTemporaryEmployeeId(
        temporaryEmployeeStore.current.id,
        true,
      );
    }
  }, [temporaryEmployeeStore.current]);

  useAsyncEffect(async () => {
    if (selectedFormEmployee) {
      await onboardingFormEmployeeFileStore.fetchByOnboardingFormEmployee(
        selectedFormEmployee.id,
        true,
      );
    }
  }, [selectedFormEmployee]);

  const getFileCountText = (files: any[] | undefined) => {
    if (!files || files.length === 0) {
      return 'No files uploaded';
    }

    if (files.length === 1) {
      return '1 file uploaded';
    }

    return `${files.length} files uploaded`;
  };

  return (
    <div style={{ paddingRight: 20 }}>
      {onboardingFormEmployeeStore.values.length === 0 && 'No forms submitted by employee'}

      <Grid className="spaced-grid" columns={2} relaxed>
        {onboardingFormEmployeeStore.values.map((onboardingFormEmployee) => {
          const hasFiles = onboardingFormEmployee.files && onboardingFormEmployee.files.length > 0;
          const fileCountText = getFileCountText(onboardingFormEmployee.files);

          return (
            <Grid.Column
              key={`form-${onboardingFormEmployee.id}`}
              mobile={16}
              tablet={16}
              computer={8}
            >
              <FileDisplayCard
                fileName={hasFiles ? fileCountText : undefined}
                azureBlobName={hasFiles ? 'preview-available' : undefined}
                fileTitle={onboardingFormEmployee.onboardingForm?.title ?? ''}
                fileTitleLink={`/onboarding/forms/templates/${onboardingFormEmployee.onboardingForm?.id}`}
                signature={onboardingFormEmployee.signature}
                showNoFileMessage={!hasFiles}
                checkIconTooltip="Employee submitted the form"
                previewIconTooltip={hasFiles ? 'View all uploaded files' : undefined}
                downloadIconTooltip={hasFiles ? 'Download files' : undefined}
                hasMultipleFiles
                isDeleted={!!onboardingFormEmployee.deleteDatetime}
                handlePreview={() => {
                  setSelectedFormEmployee(onboardingFormEmployee);
                  setShowFilesModal(true);
                }}
              />
            </Grid.Column>
          );
        })}
      </Grid>

      {showFilesModal && selectedFormEmployee && (
        <MultipleFilesPreviewModal
          files={
            selectedFormEmployee.files?.map((file) => ({
              id: file.id,
              insertDatetime: file.insertDatetime,
              fileName: file.fileName,
              azureBlobName: file.azureBlobName,
              label: file.createdByUser?.hasMinAuthority(Authority.PayrollAdmin)
                ? 'Uploaded by Admin'
                : undefined,
            })) ?? []
          }
          archivedFiles={onboardingFormEmployeeFileStore.values.map(({ id, ...file }) => ({
            id: toID(id),
            ...file,
          }))}
          visible={showFilesModal}
          formTitle={selectedFormEmployee.onboardingForm?.title || 'Form'}
          signature={selectedFormEmployee.signature}
          fetchUrl={async (fileId: number) => {
            const url = await onboardingFormEmployeeStore.fetchUrl(selectedFormEmployee.id, fileId);
            return url;
          }}
          fetchBlob={async (fileId: number) => {
            const { file } = await onboardingFormEmployeeStore.download(
              selectedFormEmployee.id,
              fileId,
            );
            return file;
          }}
          handleDownload={async (fileId: number, fileName?: string) => {
            if (fileName) {
              const { file } = await onboardingFormEmployeeStore.download(
                selectedFormEmployee.id,
                fileId,
              );

              downloadBlob(file, fileName);
            }
          }}
          handleArchive={async (fileId: number) => {
            if (temporaryEmployeeStore.current) {
              await onboardingFormEmployeeStore.deleteFile(selectedFormEmployee.id, fileId);

              const [employees] = await Promise.all([
                onboardingFormEmployeeStore.fetchByTemporaryEmployeeId(
                  temporaryEmployeeStore.current.id,
                ),
                onboardingFormEmployeeFileStore.fetchByOnboardingFormEmployee(
                  selectedFormEmployee.id,
                  true,
                ),
              ]);

              setSelectedFormEmployee(employees.find(({ id }) => id === selectedFormEmployee.id));
            }
          }}
          handleRestore={async (fileId: number) => {
            if (temporaryEmployeeStore.current) {
              await onboardingFormEmployeeStore.restoreFile(selectedFormEmployee.id, fileId);

              const [employees] = await Promise.all([
                onboardingFormEmployeeStore.fetchByTemporaryEmployeeId(
                  temporaryEmployeeStore.current.id,
                ),
                onboardingFormEmployeeFileStore.fetchByOnboardingFormEmployee(
                  selectedFormEmployee.id,
                  true,
                ),
              ]);

              setSelectedFormEmployee(employees.find(({ id }) => id === selectedFormEmployee.id));
            }
          }}
          onClose={() => {
            setShowFilesModal(false);
            setSelectedFormEmployee(undefined);
          }}
          handleUpload={async (files) => {
            if (selectedFormEmployee?.onboardingForm?.id) {
              // Upload each file and collect the responses
              const uploadedFiles = await Promise.all(
                files.map(async (file) => {
                  const formData = new FormData();
                  formData.append('file', file);

                  const response = await onboardingFormEmployeeStore.upload(
                    selectedFormEmployee.onboardingForm!.id,
                    formData,
                  );

                  return {
                    fileName: response.fileName,
                    azureBlobName: response.azureBlobName,
                  };
                }),
              );

              // Rather than overwriting the existing files using the update action,
              // add each individual file to the onboarding form employee file entity
              const newFiles = await Promise.all(
                uploadedFiles.map(async (file) => {
                  return onboardingFormEmployeeStore.createSingleFile(
                    selectedFormEmployee.id,
                    file.fileName,
                    file.azureBlobName,
                  );
                }),
              );

              // Update the selected form employee with the new files
              if (temporaryEmployeeStore.current) {
                const selectedFormEmployeeWithNewFiles = {
                  ...selectedFormEmployee,
                  files: [...newFiles, ...(selectedFormEmployee.files || [])],
                };

                setSelectedFormEmployee(selectedFormEmployeeWithNewFiles as OnboardingFormEmployee);
              }
            }
          }}
        />
      )}
    </div>
  );
});
